/**
 * Ground-truth label for one bench row — what the analysis *should* have
 * concluded about this solution.
 *
 * A label is not a verdict. A verdict grades an output that ran, one per
 * prompt execution; a label states the right answer for the row and can be
 * written before anything has run at all. Verdicts feed the scorecard; the
 * label feeds the automated scorer, which skips rows that lack one — that, and
 * not "judging is blocked", is what `unlabeled` means.
 *
 * The form is a component rather than a modal so it can sit beside the student
 * work in the row drawer: the expected assessment cannot honestly be written on
 * a screen that does not show the solution being assessed.
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../../api/adminApi';
import type { IdealAssessment, NumberRange } from '../../types';
import type { ScenarioInput } from '../../types/scenarioEvals';

// ─── Numeric (int/float) dimension: an inclusive [min,max] range. Enter one
//     value for an exact target (min === max). ─────────────────────────────
type RangeStr = { min: string; max: string };
const seedRange = (r?: NumberRange): RangeStr => ({ min: r ? String(r.min) : '', max: r ? String(r.max) : '' });
function toNumberRange(r: RangeStr): NumberRange | undefined {
    if (r.min === '' && r.max === '') return undefined;
    const min = Number(r.min === '' ? r.max : r.min);
    const max = Number(r.max === '' ? r.min : r.max);
    return Number.isNaN(min) || Number.isNaN(max) ? undefined : { min, max };
}
function RangeField({ label, range, onChange }: { label: string; range: RangeStr; onChange: (r: RangeStr) => void }) {
    return (
        <label>{label}
            <span style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                <input type="number" min={0} max={10} placeholder="min" value={range.min} onChange={e => onChange({ ...range, min: e.target.value })} />
                <span>–</span>
                <input type="number" min={0} max={10} placeholder="max" value={range.max} onChange={e => onChange({ ...range, max: e.target.value })} />
            </span>
        </label>
    );
}

/**
 * The label form itself, with no chrome of its own.
 *
 * `onSaved` fires after the write; the caller decides whether that closes a
 * modal, collapses a panel, or nothing at all. Invalidation covers both
 * surfaces — the inputs list and the bench matrix — because a label written in
 * one has to stop showing "unlabeled" in the other.
 */
export function LabelForm({ datasetId, input, onSaved, onCancel }: {
    datasetId: string;
    input: ScenarioInput;
    onSaved?: () => void;
    onCancel?: () => void;
}) {
    const qc = useQueryClient();
    // Seed from the existing label, else the captured production assessment.
    const a = input.label?.assessment ?? input.suggestedAssessment ?? {};
    const prefilled = !input.label && !!input.suggestedAssessment;
    const [value, setValue] = useState(a.correctness?.value ?? '');
    const [unit, setUnit] = useState(a.correctness?.unit ?? '');
    // One acceptable answer per line — first line is primary, the rest are alts.
    const [correctAnswer, setCorrectAnswer] = useState([a.correctLlmAnswer ?? '', ...(a.correctLlmAnswerAlts ?? [])].filter(Boolean).join('\n'));
    const [studentAnswer, setStudentAnswer] = useState([a.expectedStudentAnswer ?? '', ...(a.expectedStudentAnswerAlts ?? [])].filter(Boolean).join('\n'));
    const [finalScore, setFinalScore] = useState<RangeStr>(seedRange(a.expectedFinalScore));
    const [accuracy, setAccuracy] = useState<RangeStr>(seedRange(a.expectedCorrectnessScore));
    const [approach, setApproach] = useState<RangeStr>(seedRange(a.expectedApproachScore));
    const [confidence, setConfidence] = useState(input.label?.confidence ?? 'high');
    const [notes, setNotes] = useState(input.label?.notes ?? '');

    const save = useMutation({
        mutationFn: () => {
            const lines = (s: string) => s.split('\n').map(x => x.trim()).filter(Boolean);
            const assessment: IdealAssessment = {};
            if (value) assessment.correctness = { value, ...(unit ? { unit } : {}) } as IdealAssessment['correctness'];
            const ca = lines(correctAnswer);
            if (ca.length) { assessment.correctLlmAnswer = ca[0]; if (ca.length > 1) assessment.correctLlmAnswerAlts = ca.slice(1); }
            const sa = lines(studentAnswer);
            if (sa.length) { assessment.expectedStudentAnswer = sa[0]; if (sa.length > 1) assessment.expectedStudentAnswerAlts = sa.slice(1); }
            const fr = toNumberRange(finalScore); if (fr) assessment.expectedFinalScore = fr;
            const ar = toNumberRange(accuracy); if (ar) assessment.expectedCorrectnessScore = ar;
            const pr = toNumberRange(approach); if (pr) assessment.expectedApproachScore = pr;
            return adminApi.labelScenarioInput(datasetId, input.id, { assessment, confidence: confidence as 'high' | 'medium' | 'low', notes });
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['scenarioInputs', datasetId] });
            qc.invalidateQueries({ queryKey: ['benchMatrix', datasetId] });
            onSaved?.();
        },
    });

    return (
        <>
            {prefilled && <p className="sd-prefill">Pre-filled from the model's assessment — review and correct.</p>}
            <div className="sd-form">
                <label>Correctness verdict
                    <select value={value} onChange={e => setValue(e.target.value)}>
                        <option value="">—</option><option value="correct">correct</option><option value="incorrect">incorrect</option><option value="not_found">not_found</option>
                    </select>
                </label>
                <label>Unit
                    <select value={unit} onChange={e => setUnit(e.target.value)}>
                        <option value="">—</option><option value="correct">correct</option><option value="incorrect">incorrect</option><option value="missing">missing</option><option value="not_needed">not_needed</option>
                    </select>
                </label>
                <label>Correct answer(s) — one acceptable form per line<textarea rows={2} value={correctAnswer} onChange={e => setCorrectAnswer(e.target.value)} /></label>
                <label>Expected student answer(s) — one per line<textarea rows={2} value={studentAnswer} onChange={e => setStudentAnswer(e.target.value)} /></label>
                <RangeField label="Expected final score (0–10)" range={finalScore} onChange={setFinalScore} />
                <RangeField label="Expected accuracy score (0–10)" range={accuracy} onChange={setAccuracy} />
                <RangeField label="Expected approach score (0–10)" range={approach} onChange={setApproach} />
                <label>Confidence
                    <select value={confidence} onChange={e => setConfidence(e.target.value as 'high' | 'medium' | 'low')}>
                        <option value="high">high</option><option value="medium">medium</option><option value="low">low</option>
                    </select>
                </label>
                <label className="sd-full">Notes<textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} /></label>
            </div>
            {save.error && <p className="sd-err">{(save.error as Error).message}</p>}
            <div className="sd-modal-actions">
                {onCancel && <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>}
                <button className="btn btn-primary" disabled={save.isPending} onClick={() => save.mutate()}>
                    {save.isPending ? 'Saving…' : 'Save label'}
                </button>
            </div>
        </>
    );
}

/** The same form as a modal, for the Scenario Inputs table. */
export function LabelModal({ datasetId, input, onClose }: {
    datasetId: string; input: ScenarioInput; onClose: () => void;
}) {
    return (
        <div className="sd-overlay" onClick={onClose}>
            <div className="sd-modal" onClick={e => e.stopPropagation()}>
                <h2>Label ground truth</h2>
                <p className="sd-qfull">{input.question.text}</p>
                <LabelForm datasetId={datasetId} input={input} onSaved={onClose} onCancel={onClose} />
            </div>
        </div>
    );
}
